import React from "react";
import { useField } from "formik";
import {
  Text,
  Badge,
  Flex,
  Box,
  Theme,
  Button,
  IconButton,
  Separator,
  ScrollArea,
} from "@radix-ui/themes";
import { useState, useEffect, useRef } from "react";
import { X, ChevronDown, Pencil, Check } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/utils/cn";

export interface SelectFieldProps {
  name: string;
  type: "string" | "number";
  label: string;
  required?: boolean;
  className?: string;
  readonly?: boolean;
  disabled?: boolean;
  description?: string;
  children?: React.ReactNode;
  options?: {
    addon_before?: string | null;
    addon_after?: string | null;
    placeholder?: string;
    multiple?: boolean;
  } | null;
  isEditing?: boolean; // Parent component edit mode
  individualEditAllowed?: boolean; // Whether individual field editing is allowed
  emptyText?: string;
  onChange?: (e: string | number | string[] | number[]) => void;
  onEditSaved?: (
    name: string,
    value: string | number | string[] | number[]
  ) => void;
  onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

export interface SelectFieldItemProps {
  value: string | number;
  children: React.ReactNode;
  disabled?: boolean;
}

function SelectFieldItem({ value, children, disabled }: SelectFieldItemProps) {
  return (
    <Box
      p="2"
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        borderRadius: "var(--radius-1)",
      }}
      data-value={value.toString()}
      data-disabled={disabled ? "true" : "false"}
    >
      <Text size="2">{children}</Text>
    </Box>
  );
}

export interface SelectFieldGroupProps {
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
}

function SelectFieldGroup({
  label,
  children,
  disabled,
}: SelectFieldGroupProps) {
  return (
    <Box
      p="2"
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        borderRadius: "var(--radius-1)",
      }}
      data-label={label.toString()}
      data-disabled={disabled ? "true" : "false"}
    >
      <Text size="2" weight="medium">
        {label}
      </Text>
      <Box p="2">{children}</Box>
    </Box>
  );
}

function SelectFieldComponent({
  name,
  type,
  label,
  required = false,
  readonly = false,
  disabled = false,
  description,
  children,
  options,
  isEditing = true, // Parent edit mode
  individualEditAllowed = false,
  emptyText,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: SelectFieldProps) {
  const [field, { error, touched }, helpers] = useField(name);
  const isMultiple = options?.multiple || false;
  const [selectedValues, setSelectedValues] = useState<(string | number)[]>([]);
  const [tempSelectedValues, setTempSelectedValues] = useState<
    (string | number)[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isIndividualEditing, setIsIndividualEditing] = useState(false);
  const [maxHeight, setMaxHeight] = useState("400px");
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Calculate dynamic max height based on viewport
  useEffect(() => {
    const calculateMaxHeight = () => {
      const viewportHeight = window.innerHeight;
      const maxAllowedHeight = Math.min(400, viewportHeight * 0.4); // 40% of viewport or 400px max
      setMaxHeight(`${maxAllowedHeight}px`);
    };

    calculateMaxHeight();
    window.addEventListener("resize", calculateMaxHeight);

    return () => window.removeEventListener("resize", calculateMaxHeight);
  }, []);

  // Initialize selected values from field.value
  useEffect(() => {
    if (field.value) {
      if (isMultiple) {
        const values = Array.isArray(field.value) ? field.value : [field.value];
        setSelectedValues(values);
        setTempSelectedValues(values);
      } else {
        setSelectedValues([field.value]);
        setTempSelectedValues([field.value]);
      }
    } else {
      setSelectedValues([]);
      setTempSelectedValues([]);
    }
  }, [field.value, isMultiple]);

  // Reset individual editing state when parent edit mode changes
  useEffect(() => {
    if (isEditing) {
      setIsIndividualEditing(false);
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsIndividualEditing(true);
    setTempSelectedValues([...selectedValues]);
    setIsOpen(true); // Automatically open the dropdown
    onIndividualEditModeChanged?.(true);
  };

  const handleSave = () => {
    const newValue = isMultiple
      ? type === "number"
        ? (tempSelectedValues as number[])
        : (tempSelectedValues as string[])
      : tempSelectedValues[0] || "";
    helpers.setValue(newValue);
    setSelectedValues([...tempSelectedValues]);

    if (onChange) {
      onChange(newValue);
    }

    if (onEditSaved) {
      onEditSaved(name, newValue);
    }

    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleCancel = () => {
    setTempSelectedValues([...selectedValues]);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  // Find all SelectField.Item components in the child tree
  const findSelectItems = (children: React.ReactNode): React.ReactElement[] => {
    const items: React.ReactElement[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;

      // Check if this is a SelectField.Item
      if (child.type === SelectFieldItem) {
        items.push(child);
      }
      // Recursively search children if this component has children
      else if (child.props && child.props.children) {
        items.push(...findSelectItems(child.props.children));
      }
    });

    return items;
  };

  // Get all select items from the child tree
  const selectItems = findSelectItems(children);

  // Get all available (non-disabled) item values
  const getAllAvailableValues = () => {
    return selectItems
      .filter((item: React.ReactElement) => !item.props.disabled)
      .map((item: React.ReactElement) => {
        const value = item.props.value;
        return type === "number" ? Number(value) : value;
      });
  };

  // Handle Select All / Deselect All
  const handleSelectAll = (event?: React.MouseEvent) => {
    if (!isMultiple) return;

    // Prevent event propagation to avoid clicking through to elements behind
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const availableValues = getAllAvailableValues();
    const currentValues = isIndividualEditing
      ? tempSelectedValues
      : selectedValues;
    const allSelected = availableValues.every((val) =>
      currentValues.some((selected) => selected.toString() === val.toString())
    );

    const newValues = allSelected ? [] : availableValues;

    if (isIndividualEditing) {
      setTempSelectedValues(newValues);
    } else {
      helpers.setValue(newValues);
      setSelectedValues(newValues);

      if (onChange) {
        if (type === "number") {
          onChange(newValues as number[]);
        } else {
          onChange(newValues as string[]);
        }
      }
    }
  };

  const handleValueSelect = (
    value: string | number,
    itemDisabled: boolean,
    event?: React.MouseEvent
  ) => {
    if (itemDisabled) return;

    // Prevent event propagation to avoid clicking through to elements behind
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    let newValues;

    if (isMultiple) {
      newValues = [
        ...(isIndividualEditing ? tempSelectedValues : selectedValues),
      ];
      const valueStr = value.toString();
      const existingIndex = newValues.findIndex(
        (v) => v.toString() === valueStr
      );

      if (existingIndex >= 0) {
        newValues.splice(existingIndex, 1);
      } else {
        const parsedValue = type === "number" ? Number(value) : value;
        newValues.push(parsedValue);
      }

      if (isIndividualEditing) {
        setTempSelectedValues(newValues);
      } else {
        // Update Formik
        helpers.setValue(newValues);
        setSelectedValues(newValues);

        if (onChange) {
          if (type === "number") {
            onChange(newValues as number[]);
          } else {
            onChange(newValues as string[]);
          }
        }
      }
    } else {
      // Single selection - show selection change before closing popover
      const parsedValue = type === "number" ? Number(value) : value;

      if (isIndividualEditing) {
        setTempSelectedValues([parsedValue]);
      } else {
        helpers.setValue(parsedValue);
        setSelectedValues([parsedValue]);

        if (onChange) {
          onChange(parsedValue);
        }
      }

      // Small delay to show the selection change before closing
      setTimeout(() => {
        setIsOpen(false);
      }, 150);
    }
  };

  const removeValue = (
    valueToRemove: string | number,
    fromTemp: boolean = false
  ) => {
    if (isMultiple) {
      const currentValues = fromTemp ? tempSelectedValues : selectedValues;
      const newValues = currentValues.filter(
        (value) => value.toString() !== valueToRemove.toString()
      );

      if (fromTemp) {
        setTempSelectedValues(newValues);
      } else {
        // Update Formik
        helpers.setValue(newValues);
        setSelectedValues(newValues);

        if (onChange) {
          if (type === "number") {
            onChange(newValues as number[]);
          } else {
            onChange(newValues as string[]);
          }
        }
      }
    }
  };

  // Helper function to recursively render the children with proper selection handling
  const renderChildren = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;

      // Handle SelectFieldItem components
      if (child.type === SelectFieldItem) {
        const { value, disabled: itemDisabled } = child.props;
        const currentValues = isIndividualEditing
          ? tempSelectedValues
          : selectedValues;
        const isSelected = currentValues.some(
          (v) => v.toString() === value?.toString()
        );
        const isItemDisabled = itemDisabled || disabled;

        return (
          <Box
            key={value.toString()}
            style={getItemStyles(isSelected, isItemDisabled)}
            onClick={(e) => handleValueSelect(value, isItemDisabled, e)}
            data-testid={`select-option-${value}`}
            className={!isSelected && !isItemDisabled ? "hover:bg-gray-a3" : ""}
          >
            {child}
          </Box>
        );
      }

      // Recursively process children for container components
      if (child.props && child.props.children) {
        return React.cloneElement(
          child,
          { ...child.props },
          renderChildren(child.props.children)
        );
      }

      // Return other elements as is
      return child;
    });
  };

  // Determine current field state
  const isReadonlyMode = !isEditing && !isIndividualEditing;

  // Custom styles for the trigger button
  const triggerStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "5px 12px",
    border: "1px solid var(--gray-7)",
    borderRadius: "var(--radius-2)",
    backgroundColor: "var(--color-background)",
    color: "var(--color-text)",
    outline: "none",
    cursor:
      disabled || readonly
        ? isReadonlyMode && individualEditAllowed
          ? "pointer"
          : "not-allowed"
        : "pointer",
    opacity:
      disabled || readonly
        ? isReadonlyMode && individualEditAllowed
          ? 1
          : 0.5
        : 1,
  };

  // Custom styles for the content
  const contentStyles = {
    backgroundColor: "var(--color-panel-solid)",
    borderRadius: "var(--radius-2)",
    boxShadow: "var(--shadow-4)",
    border: "1px solid var(--gray-5)",
    width: "var(--radix-popover-trigger-width)",
    opacity: 1,
    zIndex: 999999,
    overflow: "hidden",
    pointerEvents: "auto" as const, // Ensure clicks work when body has pointer-events: none
  };

  // Create inline styles for selected and non-selected items
  const getItemStyles = (isSelected: boolean, isDisabled: boolean) => ({
    cursor: isDisabled ? "not-allowed" : "pointer",
    backgroundColor: isSelected ? "var(--accent-3)" : "transparent",
    borderRadius: "var(--radius-1)",
    opacity: isDisabled ? 0.5 : 1,
    margin: "2px 0",
    padding: "0 4px",
    transition: "background-color 0.2s",
    "&:hover":
      !isSelected && !isDisabled ? { backgroundColor: "var(--gray-a3)" } : {},
  });

  // Determine current field state
  const shouldShowEditIcon =
    !isEditing && individualEditAllowed && !isIndividualEditing;

  // Get the display values based on editing state
  const displayValues = isIndividualEditing
    ? tempSelectedValues
    : selectedValues;

  const getValueLabel = (value: string | number) => {
    let label = value.toString();

    // Find the matching SelectFieldItem to get its label
    if (children) {
      const match = selectItems.find(
        (child: React.ReactElement) =>
          child.props && child.props.value?.toString() === value.toString()
      );

      if (match && match.props && match.props.children) {
        label = React.Children.toArray(match.props.children).join(" ");
      }
    }

    return label;
  };

  // Check if all available items are selected
  const areAllSelected = () => {
    if (!isMultiple) return false;
    const availableValues = getAllAvailableValues();
    const currentValues = isIndividualEditing
      ? tempSelectedValues
      : selectedValues;
    return (
      availableValues.length > 0 &&
      availableValues.every((val) =>
        currentValues.some((selected) => selected.toString() === val.toString())
      )
    );
  };

  return (
    <Box mb="4" className="w-[100%]">
      <Box mb="2">
        {label && (
          <div className="flex items-center justify-between">
            <Text as="label" htmlFor={name} size="2" weight="medium" mb="1">
              {label} {required && <Text color="red">{" *"}</Text>}
            </Text>
          </div>
        )}

        {description && (
          <Text size="1" color="gray" mb="1">
            {description}
          </Text>
        )}
      </Box>

      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger
          ref={triggerRef}
          className={cn(isReadonlyMode && "readonly-input")}
          disabled={disabled || (readonly && !individualEditAllowed)}
          style={triggerStyles}
          data-testid="select-trigger"
          onClick={
            readonly && individualEditAllowed && !isIndividualEditing
              ? handleEdit
              : undefined
          }
        >
          <Flex gap="1" align="center" wrap="wrap" style={{ flex: 1 }}>
            {isMultiple ? (
              // Multiple select mode - show badges for selected items
              displayValues.length > 0 ? (
                <>
                  {displayValues.map((val) => (
                    <Badge key={val.toString()} variant="soft" size="1">
                      <Flex gap="1" align="center">
                        {getValueLabel(val)}
                        {!disabled && !readonly && (
                          <Box asChild>
                            <button
                              type="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation(); // Prevent popover from closing
                                removeValue(val, isIndividualEditing);
                              }}
                              aria-label={`Remove ${getValueLabel(val)}`}
                              data-testid={`remove-${val}`}
                              style={{
                                padding: "2px",
                                cursor: "pointer",
                                borderRadius: "9999px",
                                lineHeight: 0,
                                border: "none",
                                backgroundColor: "transparent",
                              }}
                            >
                              <X size={12} />
                            </button>
                          </Box>
                        )}
                      </Flex>
                    </Badge>
                  ))}
                  <Box width="4" height="5" />
                </>
              ) : (
                <Text color="gray" size="2">
                  {options?.placeholder || "Select items..."}
                </Text>
              )
            ) : // Single select mode - show selected item text
            displayValues.length > 0 ? (
              <Text
                size="2"
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {getValueLabel(displayValues[0])}
              </Text>
            ) : (
              <Text color="gray" size="2">
                {options?.placeholder || "Select an item..."}
              </Text>
            )}
          </Flex>
          {isReadonlyMode && shouldShowEditIcon ? (
            <IconButton
              size="1"
              type="button"
              variant="ghost"
              onClick={handleEdit}
              aria-label="Edit"
            >
              <Pencil className="w-4 h-4" />
            </IconButton>
          ) : (
            <ChevronDown size={16} />
          )}
        </Popover.Trigger>

        <Popover.Portal>
          <Theme>
            <Popover.Content
              align="start"
              side="bottom"
              sideOffset={4}
              style={contentStyles}
              data-testid="select-content"
              avoidCollisions={true}
              collisionPadding={8}
              onClick={(e) => e.stopPropagation()}
            >
              <ScrollArea
                style={{
                  maxHeight,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
                onWheel={(e) => e.stopPropagation()} // Prevent scroll events from bubbling up to sidebar
              >
                <Box p="2">
                  {/* Select All / Deselect All for multiple select */}
                  {isMultiple && selectItems.length > 1 && (
                    <>
                      <Box
                        p="2"
                        style={{
                          cursor: "pointer",
                          borderRadius: "var(--radius-1)",
                          transition: "background-color 0.2s",
                        }}
                        onClick={(e) => handleSelectAll(e)}
                        data-testid="select-all-option"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--gray-a3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <Text size="2" weight="medium" color="blue">
                          {areAllSelected() ? "Deselect All" : "Select All"}
                        </Text>
                      </Box>
                      <Separator size="4" style={{ margin: "4px 0" }} />
                    </>
                  )}

                  {selectItems.length > 0 ? (
                    renderChildren(children)
                  ) : (
                    <Box p="2">
                      <Text color="gray" size="2">
                        {emptyText || "No options available"}
                      </Text>
                    </Box>
                  )}
                </Box>
              </ScrollArea>
            </Popover.Content>
          </Theme>
        </Popover.Portal>
      </Popover.Root>

      {isIndividualEditing && !isEditing && (
        <Flex gap="2" mt="2" justify="start">
          <Button size="1" variant="soft" color="red" onClick={handleCancel}>
            <X width={12} height={12} style={{ marginRight: 4 }} />
            Cancel
          </Button>
          <Button size="1" variant="soft" color="green" onClick={handleSave}>
            <Check width={12} height={12} style={{ marginRight: 4 }} />
            Save
          </Button>
        </Flex>
      )}

      {error && touched && (
        <Text color="red" size="1" mt="1">
          {error}
        </Text>
      )}
    </Box>
  );
}

const SelectField = Object.assign(SelectFieldComponent, {
  Item: SelectFieldItem,
  Group: SelectFieldGroup,
});

export default SelectField;
